// Tip-sheet-grounded answer engine for the EA SHW "Ethische Etna".
//
// Shared by the full conversational page (vraagbaak.tsx) and the in-flow help
// panel embedded in the guided intake rail (nieuwe-aanvraag/). Keeping a single
// source means help gives the same grounded answers wherever it is asked.
// matching the vision's "same FAQ runs across the whole flow" principle.
//
// This is a prototype: a keyword router over distilled Tip Sheet content. Real
// deployment would swap getAgentResponse() for a tip-sheet RAG call.

export interface AgentResponse {
  text: string;
  tipSheets: string[];
}

export const SUGGESTED_QUESTIONS = [
  "Heb ik toestemming nodig van ouders bij onderzoek met minderjarigen?",
  "Wat is het verschil tussen anoniem en pseudoniem onderzoek?",
  "Welke informed consent documenten zijn verplicht voor mijn aanvraag?",
  "Mag ik deelnemers vergoeden of belonen voor hun deelname?",
  "Hoe verwerk ik audio-opnames in mijn aanvraag?",
  "Mijn studie gebruikt deception. Wat moet ik melden bij de EA SHW?",
];

export function getAgentResponse(question: string): AgentResponse {
  const q = question.toLowerCase();

  if (
    q.includes("minderjar") ||
    q.includes("ouder") ||
    q.includes("voogd") ||
    q.includes("leeftijd") ||
    q.includes("minor")
  ) {
    return {
      text: `Voor onderzoek met minderjarigen (jonger dan 18 jaar) maakt de EA SHW een strikt onderscheid op basis van anonimiteit.

**Niet-anoniem onderzoek met minderjarigen**
Wanneer deelnemers identificeerbaar zijn (via werving per e-mail, naam, of opnames) gelden beide toestemmingen:
- Ethische én juridische (GDPR) consent van de minderjarige zelf
- Ethische én juridische consent van de ouder of voogd

Iedereen die in direct contact komt met minderjarigen of hun niet-anonieme data verwerkt, heeft ook een uittreksel strafregister model 2 nodig.

**Anoniem onderzoek met minderjarigen**
Bij volledig anoniem onderzoek gelden lichtere vereisten, maar je moet de anonimiteit goed onderbouwen. Een online survey via een schoolplatform telt doorgaans als niet-anoniem, ook als je geen namen vraagt.`,
      tipSheets: ["Tip Sheet 7 & 8: Onderzoek met minderjarigen"],
    };
  }

  if (
    q.includes("informed consent") ||
    q.includes("consent formulier") ||
    q.includes("consent document") ||
    (q.includes("toestemming") && !q.includes("ouder") && !q.includes("minderjar"))
  ) {
    return {
      text: `Informed consent is verplicht voor alle onderzoek met menselijke deelnemers. De EA SHW hanteert twee niveaus.

**Ethische consent (C2)**
Verplicht voor elk onderzoek met proefpersonen. Deelnemers moeten volledig geïnformeerd zijn en vrijwillig deelnemen; schriftelijk is de norm. Gebruik het officiële EA SHW informed consent template. Onvolledige of foutieve formulieren leiden automatisch tot een voorlopig negatief advies.

**Juridische (GDPR) consent (C3)**
Verplicht als deelnemers op enig moment identificeerbaar zijn. Bij volledig anoniem onderzoek is dit niet nodig.

Beide documenten moeten klaar zijn vóór je (voorlopig) positief advies kunt ontvangen. Afwijken van schriftelijke consent, bijv. mondeling of via signalisatie, moet je motiveren in je aanvraag.`,
      tipSheets: ["Tip Sheet 2 & 3: Juridische en ethische informed consent"],
    };
  }

  if (
    q.includes("anoniem") ||
    q.includes("pseudoniem") ||
    q.includes("identificeerbaar") ||
    q.includes("gecodeerd") ||
    (q.includes("verschil") && (q.includes("anoniem") || q.includes("pseudoniem"))) ||
    q.includes("anonimiteit")
  ) {
    return {
      text: `De EA SHW maakt een strikt onderscheid tussen vier categorieën.

**Volledig anoniem**
Deelnemers zijn op geen enkel moment identificeerbaar, ook niet tijdens werving of verwerking. Alleen ethische consent is vereist. Dit is zeldzamer dan onderzoekers denken.

**Pseudoniem / gecodeerd**
Er bestaat een koppelcode waarmee heridentificatie theoretisch mogelijk is. Dit telt als niet-anoniem: ook GDPR-consent is verplicht.

**Identificeerbaar**
Namen, e-mailadressen, foto's, audio- of video-opnames zijn altijd niet-anoniem.

**Anonimisering achteraf**
Data die na afloop geanonimiseerd worden, zijn tijdens verzameling identificeerbaar. Gebruik GDPR-consent voor die fase.

Twijfel je? Gebruik het EA SHW-stroomschema 'Is mijn studie anoniem?' voor een stapsgewijze beslissing.`,
      tipSheets: [
        "Tip Sheet 4: Anonimiteit en pseudonimiteit",
        "Flowchart: Is mijn studie anoniem?",
      ],
    };
  }

  if (
    q.includes("vergoed") ||
    q.includes("beloning") ||
    q.includes("belonen") ||
    q.includes("incentive") ||
    q.includes("betaling") ||
    q.includes("lotterij") ||
    q.includes("voucher") ||
    q.includes("cadeaubon")
  ) {
    return {
      text: `Deelnemers vergoeden mag, maar de EA SHW let scherp op ongepaste beïnvloeding.

**Wat toegestaan is**
- Onkostenvergoeding (reiskosten, tijdsinvestering)
- Kleine cadeaubonnen of vouchers
- Deelname aan een loting bij eerlijke kans en redelijke prijs

**Aandachtspunten**
- De vergoeding mag nooit zo hoog zijn dat deelname aan een risicovolle studie 'de moeite waard' wordt om financiële redenen
- Bij kwetsbare groepen (studenten, mensen in financiële nood) is extra voorzichtigheid geboden: een hoge vergoeding kan de vrijwilligheid ondermijnen
- Elke vorm van vergoeding, ook non-monetair, moet beschreven zijn in je dossier: het type, het bedrag, en waarom dit proportioneel is`,
      tipSheets: ["Tip Sheet 5 & 6: Vergoeding en beloning"],
    };
  }

  if (
    q.includes("opname") ||
    q.includes("audio") ||
    q.includes("video") ||
    q.includes("recording") ||
    q.includes("transcr") ||
    q.includes("foto") ||
    q.includes("beeld")
  ) {
    return {
      text: `Bij audio-, video- of fotografische opnames gelden specifieke vereisten bovenop de standaard informed consent.

**Toestemming voor opnames**
Toestemming voor opnames moet expliciet en apart vermeld staan in het informed consent, los van de algemene deelnametoestemming. Deelnemers moeten weten dat er opgenomen wordt.

**Wat je beschrijft in je aanvraag**
- Wie toegang heeft tot de ruwe opnames
- Hoe lang de opnames bewaard worden en wanneer ze vernietigd worden
- Of en hoe ze getranscribeerd en geanonimiseerd worden
- Of hergebruik voor toekomstig onderzoek mogelijk is, en of deelnemers hiervoor apart toestemmen

**Beperkte anonimiseerbaarheid**
Stemopnames zijn zelden volledig te anonimiseren. Videobeelden al helemaal niet. Wees eerlijk hierover en claim geen anonimiteit als die technisch niet haalbaar is.`,
      tipSheets: ["Tip Sheet 12: Opnames"],
    };
  }

  if (
    q.includes("deception") ||
    q.includes("bedrog") ||
    q.includes("misleid") ||
    q.includes("debriefing") ||
    q.includes("niet volledig")
  ) {
    return {
      text: `Deception in onderzoek is toegestaan, maar vraagt een grondige onderbouwing.

**Wat je moet aantonen**
- Wetenschappelijke noodzaak: het onderzoek is alleen valide als deelnemers de opzet niet kennen
- Proportionaliteit: het bedrog is minimaal en staat in verhouding tot de wetenschappelijke meerwaarde
- Geen onnodige schade: deelnemers ondervinden geen ernstige nadelen door het misleiden

**Debriefing (verplicht)**
Na afloop moeten alle deelnemers volledig geïnformeerd worden over de werkelijke opzet. Beschrijf je debriefing-procedure in detail in je aanvraag.

**Recht op intrekking na debriefing**
Deelnemers mogen na de debriefing hun data nog intrekken. Hoe je dit mogelijk maakt, moet beschreven zijn in je protocol.`,
      tipSheets: ["Tip Sheet 13: Deception en debriefing"],
    };
  }

  if (
    q.includes("kwetsbaar") ||
    q.includes("vulnerability") ||
    q.includes("afhankelijk") ||
    q.includes("machtsasym") ||
    q.includes("patiënt") ||
    q.includes("beperking")
  ) {
    return {
      text: `Bij onderzoek met kwetsbare groepen verwacht de EA SHW extra verantwoording.

**Wie valt onder 'kwetsbaar'?**
- Mensen in afhankelijkheidsrelaties (studenten bij hun docent, werknemers bij hun werkgever)
- Mensen met cognitieve, emotionele of sociale beperkingen
- Patiënten, gevangenen of mensen in financieel precaire situaties
- Minderjarigen (zie ook Tip Sheet 7 & 8)

**Wat je in je aanvraag beschrijft**
- De aard van de kwetsbaarheid en waarom deze groep deelneemt
- Hoe je vrijwilligheid garandeert ondanks de machtsasymmetrie
- Welke extra maatregelen je neemt, bijv. tussenkomst van onafhankelijke persoon of recht op weigering zonder gevolgen

Machtsasymmetrie (bijv. student-docent of werknemer-werkgever) moet expliciet besproken worden, ook als de deelnemer formeel 'vrij' kan weigeren.`,
      tipSheets: ["Tip Sheet 9 & 10: Kwetsbaarheid"],
    };
  }

  if (
    q.includes("gdpr") ||
    q.includes("bewaartermijn") ||
    q.includes("data bewaar") ||
    q.includes("data opslaan") ||
    q.includes("vernietig") ||
    q.includes("persoonsgegevens")
  ) {
    return {
      text: `Voor de verwerking van persoonsgegevens in onderzoek gelden GDPR-verplichtingen.

**Wat de EA SHW verwacht**
- Expliciete GDPR-consent als deelnemers identificeerbaar zijn (C3 in het aanvraagformulier)
- Een beschrijving van de bewaartermijn: hoe lang worden ruwe data bewaard?
- Wie heeft toegang tot de data en hoe worden ze beveiligd?
- Wanneer en hoe worden data vernietigd?

**Bewaartermijn**
Er is geen universele termijn. UA hanteert doorgaans 10 jaar voor academisch onderzoek, maar dat moet je per studie motiveren.

**Let op bij opnames**
Audio- en video-opnames zijn identificeerbaar, ook na transcriptie. Beschrijf apart wanneer ruwe opnames vernietigd worden.`,
      tipSheets: [
        "Tip Sheet 2 & 3: Informed consent",
        "Tip Sheet 12: Opnames",
        "Tip Sheet 14 & 15: Hergebruik van data",
      ],
    };
  }

  if (
    q.includes("internationaal") ||
    q.includes("buiten belg") ||
    q.includes("buitenland") ||
    q.includes("global") ||
    q.includes("cross-cultureel")
  ) {
    return {
      text: `Bij onderzoek buiten België of in cross-culturele settings gelden aanvullende overwegingen.

**Lokale ethische toetsing**
In sommige landen is ook lokale ethische goedkeuring verplicht, naast de EA SHW-goedkeuring. Onderzoek dit vóór je start.

**Machtsongelijkheid**
Wees alert op machtsverschillen tussen jou als UA-onderzoeker en de lokale gemeenschap. De EA SHW verwacht dat je dit bespreekt: wie profiteert van het onderzoek?

**Taal en culturele aanpassing**
Informed consent formulieren moeten in de taal en op het niveau van de doelgroep zijn opgesteld. Vertalen is niet voldoende; ook culturele nuances in het concept 'toestemming' tellen mee.

**Data overdracht buiten EU**
Als data opgeslagen worden buiten de EU gelden extra GDPR-vereisten. Vermeld dit expliciet in je aanvraag.`,
      tipSheets: ["Tip Sheet 16: Global engagement"],
    };
  }

  if (
    q.includes("hergebruik") ||
    q.includes("secundair") ||
    q.includes("bestaande data") ||
    q.includes("archief")
  ) {
    return {
      text: `Hergebruik van bestaande data heeft eigen ethische vereisten.

**Twee cruciale vragen**
- Is het geplande gebruik verenigbaar met het originele doel waarvoor de data verzameld werden?
- Op welke juridische basis mag je de data hergebruiken?

**Opties voor juridische basis**
- Originele consent dekte het hergebruik al in
- Toestemming specifiek voor hergebruik alsnog vragen
- Wetenschappelijk belang (GDPR artikel 89), maar dat vereist extra waarborgen

**Volledig geanonimiseerde data**
Als data werkelijk onomkeerbaar geanonimiseerd zijn, vallen ze buiten GDPR en kan hergebruik vrij. Maar je moet de onomkeerbaarheid aantonen.

Twijfel je of hergebruik ethisch onderbouwd is? Bespreek dit vooraf met het secretariaat.`,
      tipSheets: ["Tip Sheet 14 & 15: Hergebruik van data"],
    };
  }

  if (
    q.includes("doelgroep") ||
    q.includes("werving") ||
    q.includes("rekrut") ||
    q.includes("inclusie") ||
    q.includes("exclusie") ||
    q.includes("studiepopulatie") ||
    q.includes("populatie")
  ) {
    return {
      text: `De EA SHW kijkt bij je studiepopulatie naar verschillende aspecten.

**Omvang en herkenbaarheid**
Bij kleine populaties — een specifieke beroepsgroep of een kleine school — is anonimiteit moeilijker te garanderen, ook als je geen namen vraagt. Denk na over herkenbaarheidrisico bij publicatie.

**Inclusie- en exclusiecriteria**
Beschrijf je in-/exclusiecriteria en onderbouw waarom bepaalde groepen worden uitgesloten. Zorg dat exclusie niet discriminatoir is zonder wetenschappelijke reden.

**Werving en anonimiteit**
Hoe je deelnemers werft, bepaalt mede de anonimiteit. Werving via e-mail of persoonlijk contact maakt deelname niet-anoniem, ook als de studie zelf anoniem lijkt.

**Inclusiviteit**
De EA SHW verwacht dat je nadenkt over toegankelijkheid: zijn mensen met een beperking, anderstaligen of digitaal minder vaardigen in staat deel te nemen?`,
      tipSheets: ["Tip Sheet 1 — Studiepopulatie"],
    };
  }

  return {
    text: `Bedankt voor je vraag. Dit specifieke onderwerp valt mogelijk buiten mijn huidige kennisbank, of ik heb de vraag niet volledig herkend.

**Wat ik aanraad**
— Raadpleeg de EA SHW Tip Sheets rechtstreeks — beschikbaar via het medialibrary-portaal van UA
— Gebruik de knop 'Secretariaat laten meekijken' in je aanvraag voor directe hulp
— Neem contact op met de ethische commissie via het officiële contactformulier

Ik ben een prototype gebaseerd op de EA SHW Tip Sheets 1–10, 12–16. Voor juridische of complexe gevallen is menselijk advies altijd het meest betrouwbaar.`,
    tipSheets: [],
  };
}
